import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as Chat } from "../../Global Icons/chat&support.svg";
import { useDispatch, useSelector } from "react-redux";
import { getChatListAdmin } from "../../Redux/Actions/chatAction";
import "./chatListAdmin.css";

const ChatListAdmin = ({
  setSelectedBookingId,
  chatWith,
  setChatWith,
  setSelectedPerson,
}) => {
  const [selectedBooking, setSelectedBooking] = useState(null);

  const [allChats, setAllChats] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 18,
    total: 0,
  });

  const dispatch = useDispatch();
  const listRef = useRef(null);

  const currentPageRef = useRef(1);

  const loadingRef = useRef(false);

  const setLoadingState = (val) => {
    loadingRef.current = val;
    setLoading(val);
  };

  const fetchDetails = useCallback(
    async (page, limit) => {
      if (!chatWith) return;
      setLoadingState(true);
      await dispatch(getChatListAdmin({ page, limit, chat_with: chatWith }));
      setLoadingState(false);
    },
    [dispatch, chatWith]
  );

  useEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = listEl;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 100;
      const hasMorePages =
        currentPageRef.current * pagination.pageSize < pagination.total;

      if (nearBottom && !loadingRef.current && hasMorePages) {
        const nextPage = currentPageRef.current + 1;
        currentPageRef.current = nextPage;
        setPagination((prev) => ({ ...prev, current: nextPage }));
        fetchDetails(nextPage, pagination.pageSize);
      }
    };

    listEl.addEventListener("scroll", handleScroll);
    return () => listEl.removeEventListener("scroll", handleScroll);
  }, [pagination.total, pagination.pageSize, fetchDetails]);

  useEffect(() => {
    currentPageRef.current = 1;
    setPagination({ current: 1, pageSize: 18, total: 0 });
    setAllChats([]);
    fetchDetails(1, 18);
  }, [chatWith, fetchDetails]);

  const { chats } = useSelector((state) => state.chats || {});

  useEffect(() => {
    if (!chats) return;

    if (chats?.total != null) {
      setPagination((prev) => ({ ...prev, total: chats.total }));
    }

    if (Array.isArray(chats?.data)) {
      const isFirstPage = currentPageRef.current === 1;
      setAllChats((prev) =>
        isFirstPage ? chats.data : [...prev, ...chats.data]
      );
    }
  }, [chats]);

  const chatGroups = ["users", "builders"];

  const handleChatClick = (chat) => {
    const isUser = chatWith === "users";

    const selectedDetails = {
      name: isUser ? chat?.user?.name || "" : chat?.builder?.brand_name || "",
      email: isUser ? chat?.user?.email || "" : chat?.builder?.primary_email || "",
      contactNumber: isUser
        ? chat?.user?.contact_number || ""
        : chat?.builder?.primary_contact_number || "",
      countryCode: isUser
        ? chat?.user?.country_code || ""
        : chat?.builder?.primary_country_code || "",
    };

    const id = isUser ? chat?.user?._id : chat?.builder?._id;

    setSelectedBookingId(id);
    setSelectedPerson(selectedDetails);
    setSelectedBooking(id);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const sortedChatList = [...allChats].sort((a, b) => {
    return (b.last_message_time || 0) - (a.last_message_time || 0);
  });

  const filteredChatList = sortedChatList.filter((chat) => {
    const name =
      chatWith === "users"
        ? chat?.user?.name?.toLowerCase()
        : chat?.builder?.brand_name?.toLowerCase();
    return name?.includes(searchValue.toLowerCase());
  });

  const getInitials = (name) =>
    name
      ? name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
      : "?";

  return (
    <div className="cla-panel">
      {/* ── Header ── */}
      <div className="cla-header">
        <div className="cla-header-icon">
          <Chat />
        </div>
        <h2 className="cla-header-title">Support Chats</h2>
      </div>

      {/* ── Tabs ── */}
      <div className="cla-tabs">
        {chatGroups.map((group) => (
          <button
            key={group}
            onClick={() => setChatWith(group)}
            className={`cla-tab ${chatWith === group ? "cla-tab--active" : ""}`}
          >
            {group.charAt(0).toUpperCase() + group.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Search ── */}
      <div className="cla-search-wrapper">
        <svg
          className="cla-search-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          value={searchValue}
          onChange={handleSearchChange}
          className="cla-search-input"
          placeholder="Search conversations…"
        />
      </div>

      {/* ── Count label ── */}
      <div className="cla-section-label">
        {filteredChatList.length} conversation
        {filteredChatList.length !== 1 ? "s" : ""}
      </div>

      {/* ── List (scrollable) ── */}
      <div className="cla-list" ref={listRef}>
        {filteredChatList.length === 0 ? (
          <div className="cla-empty">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <p>No conversations found</p>
          </div>
        ) : (
          filteredChatList.map((chat, index) => {
            const unreadCount = chat.unread_count || 0;
            const id =
              chatWith === "users" ? chat?.user?._id : chat?.builder?._id;
            const name =
              chatWith === "users"
                ? chat?.user?.name
                : chat?.builder?.brand_name;

            return (
              <div
                key={id || index}
                className={`cla-item ${
                  selectedBooking === id ? "cla-item--active" : ""
                }`}
                onClick={() => handleChatClick(chat)}
              >
                {/* Avatar */}
                <div className="cla-avatar">
                  {chat?.profile_image ? (
                    <img src={chat.profile_image} alt={name || "user"} />
                  ) : (
                    <span className="cla-avatar-initials">
                      {getInitials(name)}
                    </span>
                  )}
                  {unreadCount > 0 && <span className="cla-avatar-dot" />}
                </div>

                {/* Info */}
                <div className="cla-item-info">
                  <div className="cla-item-row">
                    <span className="cla-item-name">
                      {name ? name.slice(0, 24) : "Unknown"}
                    </span>
                    {unreadCount > 0 && (
                      <span className="cla-badge">{unreadCount}</span>
                    )}
                  </div>
                  <span className="cla-item-preview">
                    {chat.last_message && typeof chat.last_message === "string"
                      ? chat.last_message.slice(0, 38) +
                        (chat.last_message.length > 38 ? "…" : "")
                      : "No messages yet"}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── Loader ── */}
      {loading && (
        <div className="cla-loader">
          <span />
          <span />
          <span />
        </div>
      )}
    </div>
  );
};

export default ChatListAdmin;